import {
  createSortedRowModel,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type ColumnDef,
  type RowData,
} from "@tanstack/react-table"
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ListFilterIcon,
  type LucideIcon,
} from "lucide-react"
import {
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import { Button } from "@/primitives/button.tsx"
import { Skeleton } from "@/primitives/skeleton.tsx"
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/primitives/table.tsx"
import { cn } from "@/primitives/utils"
import { filterRows, pinnedFirst } from "./data-table.ts"
import { countOf, EmptyState, tableListingOf, type TableListing } from "./empty-state.tsx"
import { FilterMenu, SearchField, type Facet } from "./filter-bar.tsx"
import { Key } from "./key.tsx"
import { PageToolbar, Updating, useStickyTop } from "./page-chrome.tsx"
import { readTableView, writeTableView, type Sort, type TableView } from "./table-view.ts"
import { useRowNavigation } from "./use-row-navigation.ts"

/**
 * One value a row can be filtered by: `value` goes in the URL, `label` is what a Member reads, and
 * `glyph` and `secondary` stand beside it in the Filter menu, as an Avatar and a job title do.
 * `keywords` find it in the menu's search without showing, as a Member's email does.
 */
export type FacetValue = {
  value: string
  label: string
  glyph?: ReactNode
  secondary?: string
  keywords?: string
}

/**
 * One column. `cell` renders it. `sortBy` makes it sortable, by the string or number it gives each
 * row — a number sorts as a number, so $10 comes after $9 — and `firstDirection` is the way its
 * first click sorts: `desc` for activity and cost, where the most is what a Member looks for.
 * `facet` makes it filterable, by the values it gives each row: a row passes when any of them is
 * chosen, and a row passes the table's filters when it passes every column's. The choices on offer
 * are the values the rows have, so a filter can never be offered that matches nothing; `facetIcon`
 * marks it in the Filter menu and its chip, and `facetLabel` names a chosen value no row has, as
 * one from an old link is.
 *
 * The choices about looks are closed, and there is no styling passthrough: `width` (the table's
 * columns are fixed, so they never jump as rows filter or page; `fill` takes what's left),
 * `align: "end"` for a numeric column, head and cells alike, and `emphasis`, where `key` renders
 * the cell's text as a Key.
 */
export type DataTableColumn<Row extends RowData, Id extends string> = {
  id: Id
  header: string
  sortBy?: (row: Row) => string | number
  firstDirection?: "asc" | "desc"
  facet?: (row: Row) => readonly FacetValue[]
  facetIcon?: LucideIcon
  facetLabel?: (value: string) => string
  width?: keyof typeof widths
  align?: "end"
} & (
  | { cell: (row: Row) => ReactNode; emphasis?: "strong" | "muted" }
  | { cell: (row: Row) => string; emphasis: "key" }
)

const widths = { fill: "", xs: "w-20", sm: "w-28", md: "w-40", lg: "w-56" } as const

const emphases = { strong: "font-medium text-foreground", muted: "text-muted-foreground" } as const

/** Where the table sits: under the page's header, full bleed; or inside a card with others. */
const chromes = {
  page: { edge: "first:pl-6 last:pr-6", bar: "px-6" },
  card: { edge: "first:pl-4 last:pr-4", bar: "px-4" },
} as const

const pageSize = 50

/** Skeleton cell widths, fixed so the rows read as varied text yet never move between renders. */
const skeletonWidths = ["w-3/5", "w-2/5", "w-1/2", "w-3/4", "w-1/3", "w-2/3", "w-1/2", "w-2/5"]

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
})

// A click on one of these inside a row is theirs, not the row's.
const ownClicks =
  "a, button, input, label, [role=menuitem], [role=checkbox], [data-slot=tooltip-trigger]"

/*
 * The table every list in the web app is: a toolbar holding the Filter menu, its chips and a
 * search, with the count and the page's Display options at its end; sortable headers that stick
 * under the page's chrome; rows that open what they list; fifty rows to a page; and an empty state
 * that tells "nothing here yet" from "nothing matches these filters".
 *
 * What a Member is looking at — filters, search, sort and page — lives in the URL's search
 * parameters, so a reload keeps it and a link reproduces it for a colleague. The table owns its own
 * parameters and leaves the page's others alone, and its part of the URL is only as long as the
 * view's departures from the default.
 *
 * `chrome` is where it sits. `page` is a whole page's list: its toolbar is the PageToolbar, and the
 * table runs full bleed with its head stuck under it. `card` sits in a card among others, its
 * filters in a compact row of its own.
 *
 * `rows` is undefined while they load, and the table shows skeleton rows at its real geometry.
 * `pending` says a refetch is keeping the old rows on screen: they dim, and a spinner turns.
 * `dimmed` fades the rows that are there to be read rather than worked in, as an archived Project
 * is; `emptyActions` go under the empty state, such as the page's own create button. `pinned` picks
 * rows that lead the table whatever it is sorted by, as a Member's Personal Project leads the
 * Projects page. `rowHref` makes each row open a page, by a click anywhere on it or, from the
 * keyboard, j/k and Enter; `actions` gives each row a last column of hover-revealed RowActions;
 * `search` makes the rows searchable by the text it gives each, or `matches` by a rule of the
 * table's own, such as every word typed found somewhere in a person.
 */
export function DataTable<Row extends RowData, Id extends string>({
  of,
  chrome,
  rows,
  rowKey,
  columns,
  sort,
  rowHref,
  search,
  matches,
  actions,
  display,
  pending = false,
  dimmed,
  pinned,
  emptyActions,
}: {
  of: TableListing
  chrome: keyof typeof chromes
  rows: readonly Row[] | undefined
  rowKey: (row: Row) => string
  columns: readonly DataTableColumn<Row, Id>[]
  sort: Sort<Id>
  rowHref?: (row: Row) => string
  search?: (row: Row) => string
  matches?: (row: Row, query: string) => boolean
  actions?: (row: Row) => ReactNode
  display?: ReactNode
  pending?: boolean
  dimmed?: (row: Row) => boolean
  pinned?: (row: Row) => boolean
  emptyActions?: ReactNode
}) {
  const listing = tableListingOf(of)
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const stickyTop = useStickyTop()
  const body = useRef<HTMLTableSectionElement>(null)
  const { edge, bar } = chromes[chrome]

  const shape = useMemo(
    () => ({
      ...(listing.prefix && { prefix: listing.prefix }),
      facets: columns.filter((column) => column.facet).map((column) => column.id),
      sortable: columns.filter((column) => column.sortBy).map((column) => column.id),
      sort,
      searchable: Boolean(search || matches),
    }),
    [columns, sort, listing.prefix, search, matches],
  )
  const view = readTableView(params, shape)
  const showView = (next: TableView<Id, Id>) =>
    setParams((page) => writeTableView(next, shape, page), { replace: true })

  const [draft, setDraft] = useSearchDraft(view.q ?? "", (q) => showView({ ...view, q, page: 1 }))
  const { matching, filtered, counts } = filterRows({
    rows: rows ?? [],
    facets: new Map(columns.flatMap(({ id, facet }) => (facet ? [[id, facet] as const] : []))),
    search,
    matches,
    filters: view.filters,
    query: draft,
  })

  const definitions = useMemo(
    () =>
      columns.map(({ id, sortBy }): ColumnDef<typeof features, Row> => ({
        id,
        accessorFn: (row) => sortBy?.(row) ?? "",
        enableSorting: Boolean(sortBy),
        sortFn: (a, b, columnId) => {
          const [x, y] = [a.getValue(columnId), b.getValue(columnId)]
          return typeof x === "number" && typeof y === "number"
            ? x - y
            : String(x).localeCompare(String(y))
        },
      })),
    [columns],
  )

  const table = useTable({
    features,
    columns: definitions,
    data: matching,
    getRowId: rowKey,
    state: { sorting: [{ id: view.sort.column, desc: view.sort.direction === "descending" }] },
  })

  // Paged here rather than by the table library, so a page past the last — from an old link, or
  // after rows went away — shows the last page instead of an empty one.
  const sorted = pinned
    ? pinnedFirst(table.getRowModel().rows, (row) => pinned(row.original))
    : table.getRowModel().rows
  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
  const page = Math.min(view.page, pageCount)
  const shown = sorted.slice((page - 1) * pageSize, page * pageSize)

  useRowNavigation(body, { enabled: Boolean(rowHref) && shown.length > 0 })

  const clearFilters = () =>
    showView({ ...readTableView(new URLSearchParams(), shape), sort: view.sort })

  const facets: Facet[] = columns.flatMap(({ id, header, facetIcon, facetLabel }) => {
    const values = counts.get(id)
    if (!values) return []
    return [
      {
        id,
        label: header,
        icon: facetIcon ?? ListFilterIcon,
        mode: "multi",
        values: values
          .toSorted((a, b) => a.value.label.localeCompare(b.value.label))
          .map(({ value: { value, label, glyph, secondary, keywords }, count }) => ({
            id: value,
            label,
            glyph,
            secondary,
            keywords,
            count,
          })),
        chosen: view.filters[id],
        onChange: (chosen) =>
          showView({ ...view, filters: { ...view.filters, [id]: chosen }, page: 1 }),
        ...(facetLabel && { labelOf: facetLabel }),
      },
    ]
  })

  const start = (
    <>
      <FilterMenu facets={facets} onClear={clearFilters} />
      {(search || matches) && (
        <SearchField placeholder={`Search ${listing.many}`} value={draft} onChange={setDraft} />
      )}
    </>
  )
  const count = rows && (
    <span className="text-xs whitespace-nowrap text-subtle-foreground tabular-nums">
      {countOf(matching.length, of)}
    </span>
  )
  const toolbar =
    chrome === "page" ? (
      <PageToolbar
        pending={pending}
        start={start}
        end={
          <>
            {count}
            {display}
          </>
        }
      />
    ) : (
      (facets.some((facet) => facet.values.length >= 2 || facet.chosen.length > 0) ||
        search ||
        matches ||
        display) && (
        <div className={cn("flex h-9 items-center gap-2 border-b border-border-subtle", bar)}>
          {start}
          <div className="ml-auto flex items-center gap-2">
            {pending && <Updating />}
            {count}
            {display}
          </div>
        </div>
      )
    )

  if (rows && rows.length === 0)
    return (
      <div>
        {chrome === "page" && toolbar}
        <EmptyState of={of} framed={chrome === "card"} actions={emptyActions} />
      </div>
    )

  const openRow = (event: MouseEvent<HTMLTableRowElement>, href: string) => {
    if (!(event.target instanceof Element) || event.target.closest(ownClicks)) return
    if (window.getSelection()?.toString()) return
    if (event.metaKey || event.ctrlKey) window.open(href, "_blank", "noopener")
    else void navigate(href)
  }

  const sortOn = (column: DataTableColumn<Row, Id>) => {
    const active = view.sort.column === column.id
    const first = column.firstDirection === "desc" ? "descending" : "ascending"
    const direction = active
      ? view.sort.direction === "ascending"
        ? "descending"
        : "ascending"
      : first
    showView({ ...view, sort: { column: column.id, direction }, page: 1 })
  }

  const cellClass = (column: { align?: "end" }) =>
    cn(
      "overflow-hidden border-b border-border-subtle text-ellipsis",
      edge,
      column.align === "end" && "text-right",
    )

  return (
    <div>
      {toolbar}
      {rows && matching.length === 0 ? (
        <EmptyState
          of={`${of}-filtered`}
          framed={chrome === "card"}
          actions={
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <>
          <table
            data-slot="table"
            className="w-full table-fixed border-separate border-spacing-0 text-ui"
          >
            {/* On the page the head sticks, on the panel's own fill so rows pass under it; in a
                card it stays put, on the card's fill. */}
            <TableHeader className={cn(chrome === "page" ? ["sticky z-10", stickyTop] : "bg-card")}>
              <tr>
                {columns.map((column) => (
                  <TableHead
                    key={column.id}
                    data-align={column.align}
                    aria-sort={
                      view.sort.column === column.id && column.sortBy
                        ? view.sort.direction
                        : undefined
                    }
                    className={cn(
                      "group/head border-b border-border-subtle",
                      edge,
                      widths[column.width ?? "fill"],
                    )}
                  >
                    {column.sortBy ? (
                      <SortButton
                        header={column.header}
                        end={column.align === "end"}
                        direction={view.sort.column === column.id ? view.sort.direction : undefined}
                        next={column.firstDirection === "desc" ? "descending" : "ascending"}
                        onClick={() => sortOn(column)}
                      />
                    ) : (
                      column.header
                    )}
                  </TableHead>
                ))}
                {actions && (
                  <TableHead className={cn("w-12 border-b border-border-subtle", edge)}>
                    <span className="sr-only">Actions</span>
                  </TableHead>
                )}
              </tr>
            </TableHeader>
            <TableBody
              ref={body}
              className={cn("transition-opacity duration-150", pending && "opacity-70")}
            >
              {rows
                ? shown.map((row) => {
                    const href = rowHref?.(row.original)
                    return (
                      <TableRow
                        key={row.id}
                        {...(href
                          ? {
                              "data-row": "",
                              onClick: (event: MouseEvent<HTMLTableRowElement>) =>
                                openRow(event, href),
                            }
                          : {})}
                        className={cn(
                          "scroll-mt-32 border-b-0",
                          dimmed?.(row.original) && "opacity-70",
                          href &&
                            "cursor-pointer data-highlighted:bg-accent has-[[data-row-link]:focus-visible]:bg-accent",
                        )}
                      >
                        {columns.map((column, index) => (
                          <TableCell
                            key={column.id}
                            data-align={column.align}
                            className={cn(
                              cellClass(column),
                              column.emphasis !== "key" &&
                                column.emphasis &&
                                emphases[column.emphasis],
                              href &&
                                index === 0 &&
                                "relative before:absolute before:inset-y-0 before:left-0 before:w-0.5 group-has-[[data-row-link]:focus-visible]/row:before:bg-ring group-data-highlighted/row:before:bg-ring",
                            )}
                          >
                            {href && index === 0 ? (
                              <Link
                                to={href}
                                data-row-link
                                className="outline-none focus-visible:outline-none"
                              >
                                <CellContent column={column} row={row.original} />
                              </Link>
                            ) : (
                              <CellContent column={column} row={row.original} />
                            )}
                          </TableCell>
                        ))}
                        {actions && (
                          <TableCell
                            className={cn(cellClass({ align: "end" }), "overflow-visible")}
                          >
                            {actions(row.original)}
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })
                : skeletonWidths.map((width, index) => (
                    <TableRow key={index} className="border-b-0 hover:bg-transparent">
                      {columns.map((column) => (
                        <TableCell key={column.id} className={cellClass(column)}>
                          <Skeleton
                            className={cn("h-3", column.align === "end" ? "ml-auto w-10" : width)}
                          />
                        </TableCell>
                      ))}
                      {actions && <TableCell className={cellClass({ align: "end" })} />}
                    </TableRow>
                  ))}
            </TableBody>
          </table>
          {pageCount > 1 && (
            <Pager
              page={page}
              pageCount={pageCount}
              from={(page - 1) * pageSize + 1}
              to={(page - 1) * pageSize + shown.length}
              of={sorted.length}
              bar={bar}
              onPage={(next) => showView({ ...view, page: next })}
            />
          )}
        </>
      )}
    </div>
  )
}

/** How long typing in the search pauses before the URL takes what was typed. */
const searchSettles = 200

/**
 * The search as typed, ahead of the URL. The rows filter on every keystroke, but the URL is
 * written once typing settles: a URL write lands a render later, and a field bound to it straight
 * would lose a keystroke typed in between. A change to the URL from elsewhere (Clear, the back
 * button) replaces what was typed.
 */
function useSearchDraft(q: string, commit: (q: string) => void) {
  const [draft, setDraft] = useState(q)
  const settle = useEffectEvent(commit)
  useEffect(() => setDraft(q), [q])
  useEffect(() => {
    if (draft === q) return
    const timer = setTimeout(() => settle(draft), searchSettles)
    return () => clearTimeout(timer)
  }, [draft, q])
  return [draft, setDraft] as const
}

function CellContent<Row extends RowData, Id extends string>({
  column,
  row,
}: {
  column: DataTableColumn<Row, Id>
  row: Row
}) {
  return column.emphasis === "key" ? <Key value={column.cell(row)} /> : column.cell(row)
}

/**
 * A sortable column's heading: the header, and an arrow for the way it sorts — shown while it is
 * the sort, and faintly on hover otherwise, pointing the way a click would sort it.
 */
function SortButton({
  header,
  end,
  direction,
  next,
  onClick,
}: {
  header: string
  end: boolean
  direction: Sort<string>["direction"] | undefined
  next: Sort<string>["direction"]
  onClick: () => void
}) {
  const Icon = (direction ?? next) === "descending" ? ArrowDownIcon : ArrowUpIcon
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "-mx-1 inline-flex h-6 items-center gap-1 rounded-sm px-1 transition-colors duration-100 ease-snappy outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
        end && "ml-auto flex-row-reverse",
        direction && "text-foreground",
      )}
    >
      {header}
      <Icon
        aria-hidden
        className={cn(
          "size-3.5 transition-opacity duration-100",
          direction
            ? "opacity-100"
            : "opacity-0 group-hover/head:opacity-50 group-focus-within/head:opacity-50",
        )}
      />
    </button>
  )
}

/** Where the Member is among the pages, "1–50 of 68", and the way to the others. */
function Pager({
  page,
  pageCount,
  from,
  to,
  of,
  bar,
  onPage,
}: {
  page: number
  pageCount: number
  from: number
  to: number
  of: number
  bar: string
  onPage: (page: number) => void
}) {
  return (
    <nav aria-label="Pages" className={cn("flex h-10 items-center justify-between", bar)}>
      <span className="text-xs text-muted-foreground tabular-nums">
        {from}–{to} of {of}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Previous page"
          disabled={page === 1}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeftIcon />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Next page"
          disabled={page === pageCount}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRightIcon />
        </Button>
      </div>
    </nav>
  )
}
